import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import {
    Button, Card, Col, DatePicker, Divider, Empty, Form, Input, Row, Select, Space,
    Tag, Typography, message,
} from 'antd';
import {
    ArrowLeftOutlined, DeleteOutlined, PlusOutlined, RobotOutlined,
    ApartmentOutlined, SaveOutlined,
} from '@ant-design/icons';
import { ENDPOINTS } from '../helpers/api/apiConfig';
import { TRIGGER_TYPE_OPTIONS } from '../components/Operations/operationModels';
import type { OperationMissionTrigger } from '../components/Operations/operationModels';
import type { MissionItem, DeviceItem } from '../components/Operations/missionModels';
import '../style/Operations.css';

const { Text, Title } = Typography;
const { TextArea } = Input;

interface MissionStepDraft {
    key: string;
    missionId: number | null;
    deviceId: number | null;
    executionOrder: number;
    triggerType: OperationMissionTrigger;
    dependsOnKeys: string[];
}

let stepCounter = 0;
const nextKey = () => `step_${++stepCounter}`;

const OperationCreate: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [form] = Form.useForm();
    const [steps, setSteps] = useState<MissionStepDraft[]>([]);
    const [saving, setSaving] = useState(false);

    // Reference data
    const [missions, setMissions] = useState<MissionItem[]>([]);
    const [devices, setDevices] = useState<DeviceItem[]>([]);
    const [loading, setLoading] = useState(false);
    const scheduledStartAt = searchParams.get('scheduledStartAt');
    const initialScheduledStartAt = React.useMemo(() => {
        if (!scheduledStartAt) {
            return null;
        }

        const parsed = dayjs(scheduledStartAt);
        return parsed.isValid() ? parsed : null;
    }, [scheduledStartAt]);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                const [missionsRes, devicesRes] = await Promise.all([
                    axios.get(ENDPOINTS.MISSIONS, { withCredentials: true }),
                    axios.get(ENDPOINTS.GET_DEVICES, { withCredentials: true }),
                ]);
                setMissions(missionsRes.data?.data || missionsRes.data || []);
                setDevices(devicesRes.data?.data || devicesRes.data || []);
            } catch (err) {
                console.error('Failed to load reference data', err);
            } finally {
                setLoading(false);
            }
        };
        void loadData();
    }, []);

    const addStep = useCallback(() => {
        setSteps((prev) => [
            ...prev,
            {
                key: nextKey(),
                missionId: null,
                deviceId: null,
                executionOrder: prev.length + 1,
                triggerType: 'auto',
                dependsOnKeys: [],
            },
        ]);
    }, []);

    const removeStep = useCallback((key: string) => {
        setSteps((prev) =>
            prev
                .filter((s) => s.key !== key)
                .map((s, i) => ({
                    ...s,
                    executionOrder: i + 1,
                    dependsOnKeys: s.dependsOnKeys.filter((k) => k !== key),
                }))
        );
    }, []);

    const updateStep = useCallback((key: string, patch: Partial<MissionStepDraft>) => {
        setSteps((prev) => prev.map((s) => (s.key === key ? { ...s, ...patch } : s)));
    }, []);

    const handleSave = useCallback(async () => {
        try {
            const values = await form.validateFields();
            const invalidSteps = steps.filter((s) => !s.missionId);
            if (invalidSteps.length > 0) {
                message.warning('Please select a mission for every step');
                return;
            }
            if (steps.length === 0) {
                message.warning('Add at least one mission step');
                return;
            }

            const keyToIndex = new Map<string, number>();
            steps.forEach((s, i) => keyToIndex.set(s.key, i));

            const payload = {
                name: values.name,
                description: values.description || undefined,
                scheduledStartAt: values.scheduledStartAt ? values.scheduledStartAt.toISOString() : null,
                missions: steps.map((s) => ({
                    missionId: s.missionId!,
                    deviceId: s.deviceId || undefined,
                    executionOrder: s.executionOrder,
                    triggerType: s.triggerType,
                    dependsOnOperationMissionIds: s.dependsOnKeys
                        .map((k) => {
                            const idx = keyToIndex.get(k);
                            return idx !== undefined ? idx + 1 : null;
                        })
                        .filter((v): v is number => v !== null),
                })),
            };

            setSaving(true);
            await axios.post(ENDPOINTS.OPERATIONS, payload, { withCredentials: true });
            message.success('Operation created');
            navigate('/operations');
        } catch (err: any) {
            if (err?.response?.data?.message) {
                message.error(err.response.data.message);
            }
            // form validation failure is silent
        } finally {
            setSaving(false);
        }
    }, [form, steps, navigate]);

    return (
        <div className="operations-page">
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {/* Back navigation */}
                <Button
                    type="link"
                    icon={<ArrowLeftOutlined />}
                    className="operations-back-button"
                    onClick={() => navigate('/operations')}
                >
                    Back to Operations
                </Button>

                {/* Header */}
                <Row justify="space-between" align="middle" gutter={[16, 16]}>
                    <Col flex="auto">
                        <Space align="center" size={12}>
                            <ApartmentOutlined style={{ fontSize: 24, color: '#1677ff' }} />
                            <Title level={3} style={{ margin: 0 }}>Create operation</Title>
                        </Space>
                        <Text type="secondary" style={{ display: 'block', marginTop: 4, marginLeft: 36 }}>
                            Choose reusable mission templates, order them, and optionally schedule the run
                        </Text>
                    </Col>
                    <Col>
                        <Space>
                            <Button onClick={() => navigate('/operations')}>Cancel</Button>
                            <Button
                                type="primary"
                                icon={<SaveOutlined />}
                                onClick={() => void handleSave()}
                                loading={saving}
                            >
                                Create operation
                            </Button>
                        </Space>
                    </Col>
                </Row>

                {/* Operation details card */}
                <Card
                    size="small"
                    title="Operation details"
                    className="operation-detail-info-card"
                >
                    <Form
                        form={form}
                        layout="vertical"
                        size="middle"
                        initialValues={{ scheduledStartAt: initialScheduledStartAt }}
                    >
                        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                            Operations do not need a separate target area. Each mission step already defines its field, route, device, and commands.
                        </Text>
                        <Row gutter={16}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="name"
                                    label="Operation name"
                                    rules={[{ required: true, message: 'Required' }]}
                                >
                                    <Input placeholder="e.g. Full pitch scan" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item name="scheduledStartAt" label="Scheduled start (optional)">
                                    <DatePicker showTime style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item name="description" label="Description">
                            <TextArea rows={3} placeholder="Describe the operation…" />
                        </Form.Item>
                    </Form>
                </Card>

                {/* Mission steps card */}
                <Card
                    size="small"
                    title={
                        <Space>
                            <ApartmentOutlined />
                            <span>Mission steps</span>
                            <Tag>{steps.length}</Tag>
                        </Space>
                    }
                    className="operation-detail-missions-card"
                    extra={
                        <Button
                            type="primary"
                            ghost
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={addStep}
                        >
                            Add step
                        </Button>
                    }
                >
                    {steps.length === 0 ? (
                        <Empty
                            description="No mission steps added yet"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                        >
                            <Button type="dashed" icon={<PlusOutlined />} onClick={addStep}>
                                Add mission step
                            </Button>
                        </Empty>
                    ) : (
                        <Space direction="vertical" style={{ width: '100%' }} size={12}>
                            {steps.map((step, idx) => (
                                <Card
                                    key={step.key}
                                    size="small"
                                    className="operation-step-card"
                                    title={
                                        <Space>
                                            <Tag color="blue">#{idx + 1}</Tag>
                                            <Text strong style={{ fontSize: 13 }}>
                                                {step.missionId
                                                    ? missions.find((m) => m.id === step.missionId)?.name || `Mission #${step.missionId}`
                                                    : 'Select mission…'}
                                            </Text>
                                        </Space>
                                    }
                                    extra={
                                        <Button
                                            type="text"
                                            danger
                                            size="small"
                                            icon={<DeleteOutlined />}
                                            onClick={() => removeStep(step.key)}
                                        />
                                    }
                                >
                                    <Row gutter={[12, 12]}>
                                        <Col xs={24} md={8}>
                                            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                                                Mission
                                            </Text>
                                            <Select
                                                style={{ width: '100%' }}
                                                showSearch
                                                placeholder="Select mission"
                                                optionFilterProp="label"
                                                loading={loading}
                                                value={step.missionId}
                                                onChange={(val) => updateStep(step.key, { missionId: val })}
                                                options={missions.map((m) => ({
                                                    value: m.id,
                                                    label: `${m.name} (${m.status})`,
                                                }))}
                                            />
                                        </Col>
                                        <Col xs={24} md={8}>
                                            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                                                <RobotOutlined /> Device (override)
                                            </Text>
                                            <Select
                                                style={{ width: '100%' }}
                                                allowClear
                                                showSearch
                                                placeholder="Use mission default"
                                                optionFilterProp="label"
                                                loading={loading}
                                                value={step.deviceId}
                                                onChange={(val) => updateStep(step.key, { deviceId: val })}
                                                options={devices.map((d) => ({
                                                    value: d.id,
                                                    label: d.label,
                                                }))}
                                            />
                                        </Col>
                                        <Col xs={12} md={4}>
                                            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                                                Trigger
                                            </Text>
                                            <Select
                                                style={{ width: '100%' }}
                                                value={step.triggerType}
                                                onChange={(val) => updateStep(step.key, { triggerType: val })}
                                                options={TRIGGER_TYPE_OPTIONS.map((t) => ({
                                                    value: t.value,
                                                    label: t.label,
                                                }))}
                                            />
                                        </Col>
                                        <Col xs={12} md={4}>
                                            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                                                Depends on
                                            </Text>
                                            <Select
                                                mode="multiple"
                                                style={{ width: '100%' }}
                                                placeholder="None"
                                                value={step.dependsOnKeys}
                                                onChange={(val) => updateStep(step.key, { dependsOnKeys: val })}
                                                options={steps
                                                    .filter((s) => s.key !== step.key)
                                                    .map((s) => ({
                                                        value: s.key,
                                                        label: `#${steps.indexOf(s) + 1} ${
                                                            s.missionId
                                                                ? missions.find((m) => m.id === s.missionId)?.name || ''
                                                                : '(empty)'
                                                        }`,
                                                    }))}
                                            />
                                        </Col>
                                    </Row>
                                </Card>
                            ))}

                            <Button
                                type="dashed"
                                icon={<PlusOutlined />}
                                onClick={addStep}
                                block
                            >
                                Add mission step
                            </Button>
                        </Space>
                    )}
                </Card>
            </Space>
        </div>
    );
};

export default OperationCreate;
